import { useCallback, useEffect, useRef } from "react";
import { useState } from "react";
import Cropper from "react-easy-crop";
import Preloader from "./Preloader";

const ModalResizeImage = ({ setShowModalResizeImage, img, funVaciarFoto, showPopup, setFormulario, formulario, name, fun }) => {
    const [image, setImage] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [aspectHorizontal, setAspectHorizontal] = useState(11 / 11);
    const [aspectVertical, setAspectVertical] = useState(12 / 19);
    const [vertical, setVertical] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [minZoom, setMinZoom] = useState(1);
    const cropContainerRef = useRef(null);

    const createImage = (url) => {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.addEventListener("load", () => {
                // Calculamos el zoom mínimo basado en las dimensiones de la imagen
                const containerWidth = cropContainerRef.current?.clientWidth || 400;
                const containerHeight = cropContainerRef.current?.clientHeight || 400;
                const minZoomWidth = containerWidth / image.width;
                const minZoomHeight = containerHeight / image.height;
                setMinZoom(Math.max(minZoomWidth, minZoomHeight, 0.5)); // 0.5 es el mínimo absoluto
                resolve(image);
            });
            image.addEventListener("error", (error) => reject(error));
            image.setAttribute("crossOrigin", "anonymous");
            image.src = url;
        });
    };

    const handleClose = () => {
        setImage(null);
        setZoom(1);
        setCroppedAreaPixels(null);
        setLoading(false);
        setRotation(0);
        setCrop({ x: 0, y: 0 });
        funVaciarFoto();
        showPopup(false, setShowModalResizeImage);
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);
        ctx.translate(-canvas.width / 2, -canvas.height / 2);

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        ctx.restore();

        return new Promise((resolve) => {
            canvas.toBlob((file) => {
                resolve(file);
                setFormulario({ ...formulario, photo: file });
            }, "image/jpeg", 0.9);
        });
    };

    useEffect(() => {
        if (img) {
            setImage(img);
        }
    }, [img]);

    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
                <div 
                    className="relative w-full max-w-4xl mx-auto bg-white rounded-lg shadow-xl"
                    style={{ maxHeight: "90vh" }}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b">
                        <h3 className="text-xl md:text-2xl font-semibold text-blue-900">
                            Recortar imagen
                        </h3>
                    </div>
                    
                    {/* Body */}
                    <div className="relative overflow-hidden">
                        {/* Controls */}
                        <div className="p-4 space-y-4">
                            <div className="flex flex-col space-y-2">
                                <label className="text-gray-600 text-sm">Zoom ({zoom.toFixed(1)})</label>
                                <input
                                    className="w-full"
                                    type="range"
                                    min={minZoom}
                                    max="3"
                                    step="0.1"
                                    value={zoom}
                                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                                />
                            </div>
                            
                            <div className="flex flex-col space-y-2">
                                <label className="text-gray-600 text-sm">Rotación ({rotation.toFixed(0)}°)</label>
                                <input
                                    className="w-full"
                                    type="range"
                                    min="0"
                                    max="360"
                                    step="1"
                                    value={rotation}
                                    onChange={(e) => setRotation(parseInt(e.target.value))}
                                />
                            </div>
                        </div>
                        
                        {/* Cropper Container */}
                        <div 
                            ref={cropContainerRef}
                            className="relative"
                            style={{ 
                                height: "50vh",
                                minHeight: "300px",
                                width: "100%"
                            }}
                        >
                            <Cropper
                                image={image}
                                crop={crop}
                                aspect={vertical ? aspectVertical : aspectHorizontal}
                                onCropChange={setCrop}
                                zoom={zoom}
                                minZoom={minZoom}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                                rotation={rotation}
                                onRotationChange={setRotation}
                                cropShape="rect"
                                restrictPosition={true}
                                style={{
                                    containerStyle: {
                                        backgroundColor: "#f0f0f0"
                                    }
                                }}
                            />
                            {loading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                                    <Preloader />
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Footer */}
                    <div className="flex flex-col sm:flex-row justify-center p-4 space-y-2 sm:space-y-0 sm:space-x-4 border-t">
                        <button
                            className="px-6 py-2 bg-Naranja-Ticongle text-white font-medium rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
                            type="button"
                            onClick={() => {
                                if (!croppedAreaPixels) {
                                    alert("Por favor, seleccione un área para recortar");
                                    return;
                                }
                                setLoading(true);
                                getCroppedImg(image, croppedAreaPixels, rotation)
                                    .then((res) => {
                                        fun && fun(res);
                                        showPopup(false, setShowModalResizeImage);
                                        setLoading(false);
                                    })
                                    .catch((error) => {
                                        console.error("Error al recortar la imagen:", error);
                                        setLoading(false);
                                    });
                            }}
                            disabled={loading}
                        >
                            {loading ? "Procesando..." : "Recortar"}
                        </button>
                        <button
                            className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
            
            {/* Backdrop */}
            <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
        </>
    );
}

export default ModalResizeImage;